import React, { createContext, useContext, useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { ErrorTypes } from '@fabric-ui/components';
import { envVariables, noop } from '@fabric-ui/utils';
import { useMutation } from '@tanstack/react-query';
import {
  fetchSession,
  getAccountTokenFromLS,
  getParsedAccountTokenMap,
  isZidSupported,
  overrideAccountIdUserAttributeV2,
  removeCognitoLS,
  setDDUser,
  setKFuseUser,
  shouldSkipUILogin,
  signIn,
} from '@utils/auth.utils';
import { convertFromSecondsToMilliseconds } from '@utils/utils';
import * as Auth from 'aws-amplify/auth';
import { decodeJWT } from 'aws-amplify/auth';
import { useSnackbar } from 'notistack';
import { AuthenticationProvider, AuthPlatform, convertSessionToUserInfo, UserInfo } from '../auth/auth.types';
import { getAuthProviderByType } from '../auth/authenticationProviderFactory';
import { AuthEvent, AuthHub } from '../auth/AuthHub';
import { extractUserPropertiesFromToken } from '../auth/cognitoAuthentication';
import { startAuthHubAdapter } from '../auth/CognitoAuthHubAdapater';
import { ZidAuthSessionStorageKeys } from '../auth/zid-oidc/tokenUtils';
import { defaultUser, useRouterContext } from '../RouterContext';
import { useNavigate, useSearchParams } from '../utils/AvRouter';
import { isExportMode } from '../utils/exportDashboards.utils';
import {
  AuthStorageKeys,
  getAccountSessionFromLs,
  getAllLSKeys,
  setAccountSessionInLs,
  setAccountTokenInLs,
} from '../utils/localStorageAuthUtils.utils';
import { useAccountId } from '../utils/router.utils';
import { shouldRecordSession } from '../utils/rum.utils';
import { sharedWorkerService } from '../workers/sharedWorkerService';

export const authContext = createContext<AuthContextType>({} as AuthContextType);
type MapValidTokenToIdentifierAndTtl = { minutesTtl: number; tokenIdentifier: string; token: string }[];

export function ProvideAuth({ children, isCurrentlyLoggingIn }) {
  const auth = useProvideAuth(isCurrentlyLoggingIn);
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

export const useAuth = () => useContext(authContext);
const useProvideAuth = (isCurrentlyLoggingIn: boolean = false) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [{ code }] = useSearchParams();
  const authConfig = JSON.parse(localStorage.getItem(AuthStorageKeys.authConfig) || '{}');
  const pathAccountId = useAccountId();
  const authPlatform = getAuthProviderByType(
    shouldSkipUILogin() ? getAccountSessionFromLs(pathAccountId)?.authProvider : authConfig.provider
  );
  const { setAuthState, setIsLoading } = useRouterContext();

  const localAuth = envVariables.VITE_ENDPOINT_FOR_TOKEN;

  const useLocalAccessToken = () =>
    useMutation<{ jwt: any; session: UserInfo }, Error, { accountId: string; email: string }>({
      mutationKey: ['local-token'],
      mutationFn: ({ accountId, email }) =>
        fetch(`${localAuth}?email=${email}&accountId=${accountId}`).then(async res => {
          const { jwt, session }: { jwt: any; session: UserInfo } = await res.json();
          const formattedJwt = { payload: parseJwt(jwt), toString: () => jwt };
          if (res.ok) {
            return {
              jwt: formattedJwt,
              session: authPlatform.formatLocalSessionObject({ jwt: formattedJwt.payload, session }),
            };
          }
          throw new Error('No access Token received');
        }),
    });
  const { mutateAsync: getLocalToken } = useLocalAccessToken();

  const isTokenExpired = (jwtToken: string) => {
    const exp = Auth.decodeJWT(jwtToken)?.payload?.exp;
    return exp && convertFromSecondsToMilliseconds(exp) - 30000 < Date.now();
  };

  const validateUser = async (retry: boolean = false) => {
    const hasCodeInTheUrl = code;
    try {
      // throws an error if the user is not authenticated
      await (isZidSupported() ? authPlatform.isUserAuthenticated() : Auth.getCurrentUser());
      setIsLoading(false);
      return true;
    } catch (error) {
      console.error(`${ErrorTypes.Authentication}: No user is found. retry ${retry}: ${error}. LS: ${getAllLSKeys()}`);
      setAuthStateFromToken(null);
      if (!hasCodeInTheUrl) {
        if (retry) {
          setIsLoading(false);
          console.error(`${ErrorTypes.Authentication}: No Token after retry: ${JSON.stringify(error)}. LS: ${getAllLSKeys()}`);
          await logoutFromAvalor({
            logout: isZidSupported() ? authPlatform.clearLS : removeCognitoLS,
            loginWithRedirect: noop,
            redirectBack: false,
          });
          window.location.reload();
          return false;
        }

        return validateUser(true);
      }

      return false;
      // if there is code param, lets wait for Hub.listen to get the signInWithRedirect event
    }
  };

  const onFirstLoad = async () => {
    const isAuthFlowInProgress =
      (authPlatform && authPlatform.provider === AuthPlatform.ZID && !!sessionStorage.getItem(ZidAuthSessionStorageKeys.oauthState)) ||
      ((!authPlatform || authPlatform.provider !== AuthPlatform.ZID) && isCurrentlyLoggingIn);

    if (isAuthFlowInProgress) {
      return;
    }

    await getAccountToken(pathAccountId);
    setIsLoading(false);
  };

  const parseJwt = token => {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch {
      return null;
    }
  };

  const getEmailForLocalAuth = async (): Promise<string | undefined> => {
    if (isZidSupported()) {
      const res = await authPlatform.fetchSession({});
      return res.userInfo?.email;
    }
    const res = await fetchSession({});
    return res.tokens?.idToken?.payload.email?.toString();
  };

  const sendTokenRequest = async (isInitialLoad: boolean, accountId: string) => {
    if (authPlatform.provider !== AuthPlatform.ZID && localAuth) {
      const email = await getEmailForLocalAuth();
      if (email) {
        const tokenRes: { jwt: any; session: UserInfo } = await getLocalToken({ email, accountId });
        const tokenAccountId = tokenRes.jwt.account_id || tokenRes?.session?.accountId;
        if (!accountId) {
          navigate({ from: '/', to: '/$accountId', params: prev => ({ ...prev, accountId: tokenAccountId }), ignoreBlocker: true });
        }

        return {
          accountToken: tokenRes.jwt,
          userInfo: tokenRes?.session,
          defaultAccountId:
            tokenRes?.session?.currentAccountCustomerMainAccount || tokenRes.jwt.payload.current_account_customer_main_account,
        };
      }
    }

    if (isInitialLoad) {
      return isZidSupported()
        ? authPlatform.fetchSession({ forceRefresh: false, firstLogin: true })
        : fetchSession({ forceRefresh: false, firstLogin: true });
    }

    return globalThis.navigator.locks.request('cognito-resource', { mode: 'exclusive' }, async () => {
      const session = isZidSupported() ? await authPlatform.fetchSession({}) : await fetchSession({});

      const tokenAccountId = getAccountIdFromSession(session);

      if (tokenAccountId && accountId !== tokenAccountId) {
        datadogRum.addAction(`Overriding user's account attribute from account`, {
          tokenAccountId,
          accountId,
          typeofAccountId: typeof accountId,
        });
        if (isZidSupported()) {
          await authPlatform.overrideAccountId(accountId);
        } else {
          await overrideAccountIdUserAttributeV2(accountId);
        }
        const updatedSession = isZidSupported() ? await authPlatform.fetchSession({}) : await fetchSession({});
        return updatedSession;
      }

      return session;
    });
  };

  const getAccountIdFromSession = (session: any): string | undefined =>
    isZidSupported() ? session?.userInfo?.accountId : session?.tokens?.idToken?.payload?.account_id;

  const getAccountTokenFromSession = (session: any): string | undefined =>
    isZidSupported() ? session?.accountToken : session?.tokens?.idToken;

  const getDefaultAccountIdFromSession = (session: any): string | undefined =>
    isZidSupported() ? session?.defaultAccountId : session?.tokens?.idToken?.payload?.default_account_id;

  const getUserIdFromSession = (session: any): string | undefined =>
    isZidSupported() ? session?.userInfo?.userId : session?.tokens?.idToken?.payload?.avalor_user_id;

  const getTokenForAccountV2 = async (accountId: string, attempts: number, isInitialLoad: boolean = false) => {
    if (shouldSkipUILogin()) {
      const token = getAccountTokenFromLS(accountId);
      const session = getAccountSessionFromLs(accountId);
      datadogRum.addAction(`exporter debug`, { token, session });
      return isZidSupported()
        ? { accountToken: { payload: parseJwt(token), toString: () => token }, session }
        : { payload: parseJwt(token), toString: () => token };
    }

    const res = await sendTokenRequest(isInitialLoad, accountId);

    const accountToken = isZidSupported() ? getAccountTokenFromSession(res) : res.tokens?.idToken;
    const defaultAccountId = isZidSupported() ? getDefaultAccountIdFromSession(res) : res.tokens?.idToken?.payload?.default_account_id;
    const accountIdFromAuth = isZidSupported() ? getAccountIdFromSession(res) : res.tokens?.idToken?.payload?.account_id;

    if (accountToken && (accountIdFromAuth === accountId || !accountId)) {
      return isZidSupported() ? { accountToken, session: res.userInfo } : accountToken;
    }
    if (attempts < 1) {
      return getTokenForAccountV2(accountId, attempts + 1, false);
    }
    // This mostly happen to the e2e user, when the account is not the requested account but not the default account
    if (authPlatform?.provider !== AuthPlatform.ZID && accountIdFromAuth !== defaultAccountId) {
      if (attempts > 3) {
        console.error(
          `${
            ErrorTypes.Authentication
          }: attempt ${attempts} token is not valid for account ${accountId} or the default account, token account ${
            accountToken?.payload.account_id
          }. LS: ${getAllLSKeys()}`
        );
      }
      return getTokenForAccountV2(accountId, attempts + 1, false);
    }
    if ((res as any)?.error) {
      if (attempts > 1) {
        console.error(
          `${ErrorTypes.Authentication}: attempt ${attempts} for getting token with error ${(res as any)?.error}. LS: ${getAllLSKeys()}`
        );
        return null;
      }
      return getTokenForAccountV2(accountId, attempts + 1, false);
    }

    if (!accountToken) {
      console.error(
        `${ErrorTypes.Authentication}: token received empty for account ${accountId} response: ${JSON.stringify(
          res
        )}. LS: ${getAllLSKeys()}`
      );
      window.location.reload();
      return null;
    }

    datadogRum.addAction(`switch for default account for user`, { userId: getUserIdFromSession(res) });
    enqueueSnackbar('No Permissions To Requested Account', { variant: 'error' });
    if (isZidSupported()) {
      await authPlatform.overrideAccountId(defaultAccountId);
    } else {
      await overrideAccountIdUserAttributeV2(defaultAccountId);
    }
    navigate({ from: '/', to: defaultAccountId as string, ignoreBlocker: true });

    return isZidSupported() ? { accountToken, session: res.userInfo } : accountToken;
  };

  const setAuthStateFromToken = token => {
    if (token) {
      const formattedUserMetadata = extractUserPropertiesFromToken(token.payload);
      setAuthState({ user: formattedUserMetadata, isAuthenticated: !!token });
      if (shouldRecordSession && !isExportMode()) {
        setDDUser({ ...formattedUserMetadata, role: token.role_id });
        setKFuseUser({ ...formattedUserMetadata, role: token.role_id });
      }
    } else {
      setAuthState({ user: { userName: '', email: '', userId: '' }, isAuthenticated: !!token });
    }

    setIsLoading(false);
  };

  const setAuthStateFromSession = (token, session) => {
    if (isExportMode()) {
      datadogRum.addAction(`exporter debug set auth state`, { token, session });
    }
    if (token && session) {
      const formattedUserMetadata = convertSessionToUserInfo(session);
      if (isExportMode()) {
        datadogRum.addAction(`exporter debug user data`, { formattedUserMetadata, token });
      }
      setAuthState({ user: formattedUserMetadata, isAuthenticated: !!token });
      if (shouldRecordSession && !isExportMode()) {
        setDDUser({ ...formattedUserMetadata, role: token.role_id });
        setKFuseUser({ ...formattedUserMetadata, role: token.role_id });
      }
    } else {
      setAuthState({ user: defaultUser, isAuthenticated: !!token });
    }

    setIsLoading(false);
  };

  useEffect(() => {
    const handleWorkerMessage = async data => {
      datadogRum.addAction(`Shared worker event`, { sharedWorkerAction: data.action, accountId: data.accountId });
      if (data.action === 'updateUserAuthState') {
        await refreshAccessToken(data.accountId);
        datadogRum.addAction(`Shared worker - refresh token in this tab for account: `, data.accountId);
        // eslint-disable-next-line
        console.log('refresh token in this tab for account: ', data.accountId);
      }
    };

    sharedWorkerService.onMessage(data => handleWorkerMessage(data));

    return () => {
      window.removeEventListener('message', handleWorkerMessage);
    };
  }, [pathAccountId]);

  useEffect(() => {
    if (shouldSkipUILogin()) {
      return () => {};
    }
    // eslint-disable-next-line custom-rules/no-let
    let userTimeoutId: ReturnType<typeof setTimeout> | null = null;
    const stopAmplifyAdapter = startAuthHubAdapter();
    const unsubscribeFromAuthHub = AuthHub.listen(({ event, data }: AuthEvent) => {
      switch (event) {
        case 'customOAuthState':
          setTimeout(() => {
            const route = decodeURIComponent(data);
            if (route !== '/') {
              navigate({ to: route, ignoreBlocker: true });
            }
          });
          break;
        case 'signInWithRedirect':
          // Cancel the timeout if we hit this case
          if (userTimeoutId) {
            clearTimeout(userTimeoutId);
            userTimeoutId = null; // Reset the timeout ID
          }
          onFirstLoad();
          break;
        case 'signInWithRedirect_failure': {
          const errorText = JSON.stringify(data.error.message);
          const underlyingError = JSON.stringify(data.error.underlyingError);
          logoutFromAvalor({
            logout: isZidSupported() ? authPlatform.clearLS : removeCognitoLS,
            loginWithRedirect: noop,
            redirectBack: false,
          });
          navigate({ to: '/$accountId', params: { pathAccountId }, ignoreBlocker: true });
          console.error(`${ErrorTypes.Authentication}: LOGIN FAIL: ${errorText} ${underlyingError}. LS: ${getAllLSKeys()}`);
          window.location.reload();
          break;
        }
        case 'tokenRefresh_failure': {
          const errorText = JSON.stringify(data.error.message);
          const underlyingError = JSON.stringify(data.error.underlyingError);
          logoutFromAvalor({
            logout: isZidSupported() ? authPlatform.clearLS : removeCognitoLS,
            loginWithRedirect: noop,
            redirectBack: false,
          });
          console.error(`${ErrorTypes.Authentication}: REFRESH TOKEN FAIL: ${errorText} ${underlyingError}. LS: ${getAllLSKeys()}`);
          window.location.reload();
          break;
        }
        default:
          break;
      }
    });

    // Set the timeout for getUser
    userTimeoutId = setTimeout(() => {
      if (code) {
        console.error(`${ErrorTypes.Authentication}: activate getUser after setTimeout. LS: ${getAllLSKeys()}`);
      }
      return onFirstLoad();
    }, 1000);

    return () => {
      unsubscribeFromAuthHub();
      stopAmplifyAdapter();
      sharedWorkerService.terminate();
      window.removeEventListener('beforeunload', () => {
        sharedWorkerService.notifyWorkerOnUnloadEvent(pathAccountId);
      });
    };
  }, []);

  const handleAuthSuccess = async (decodedToken, session?) => {
    if (isExportMode()) {
      datadogRum.addAction(`exporter debug handle auth`, { decodedToken, session });
    }
    const accountFromToken = isZidSupported() ? session?.accountId : decodedToken?.payload?.account_id;
    if (accountFromToken) {
      if (!pathAccountId) {
        navigate({ from: '/', to: '/$accountId', params: prev => ({ ...prev, accountId: accountFromToken }), ignoreBlocker: true });
      }
      setAccountTokenInLs({ accountId: accountFromToken, token: decodedToken.toString() });
      setAccountSessionInLs({ session, accountId: accountFromToken });
    }

    if (isZidSupported()) {
      setAuthStateFromSession(decodedToken, session);
    } else {
      setAuthStateFromToken(decodedToken);
    }
  };

  const getAccountToken = async (accountId: string, forceRefetch = false) => {
    const accountToken = getAccountTokenFromLS(accountId);
    const shouldRefetch = !accountToken || isTokenExpired(accountToken) || forceRefetch;
    const isAuthenticated = !!accountToken || (await validateUser());

    if (shouldRefetch && isAuthenticated) {
      const token = await getTokenForAccountV2(accountId, 0, !accountToken && !forceRefetch);
      if (isZidSupported()) {
        await handleAuthSuccess(token?.accountToken, token?.session);
      } else {
        await handleAuthSuccess(token);
      }
      sharedWorkerService.notifyWorkerAboutTokenRefresh(pathAccountId);
    } else if (isAuthenticated) {
      await handleAuthSuccess(Auth.decodeJWT(accountToken), isZidSupported() ? getAccountSessionFromLs(accountId) : null);
    }

    setIsLoading(false);
    return getAccountTokenFromLS(accountId);
  };

  const signOut = async () => {
    try {
      if (isZidSupported()) {
        await authPlatform.signOut();
      } else {
        await Auth.signOut();
      }
      setAuthState({ user: defaultUser, isAuthenticated: false });
      return { success: true, message: '' };
    } catch {
      return {
        success: false,
        message: 'LOGOUT FAIL',
      };
    }
  };

  const mapValidTokenToIdentifierAndTtl = tokenMap =>
    Object.values(tokenMap)
      .map(value => {
        const { payload } = decodeJWT(String(value));
        const currentTime = Math.floor(Date.now() / 1000);
        const { exp, jti } = payload;

        if (!exp) {
          return null;
        }

        const ttlInSeconds = exp - currentTime;

        if (ttlInSeconds > 0) {
          const ttlInMinutes = Math.ceil(ttlInSeconds / 60);
          return { tokenIdentifier: jti, token: value, minutesTtl: ttlInMinutes };
        }

        return null;
      })
      .filter(Boolean) as MapValidTokenToIdentifierAndTtl;

  async function blackListTokens(tokenMap: any, retry = 0) {
    const validTokens: MapValidTokenToIdentifierAndTtl = mapValidTokenToIdentifierAndTtl(tokenMap);
    const firstValidToken = validTokens.length > 0 ? validTokens[0]?.token : null;
    const requestBody = JSON.stringify(
      validTokens.map(({ tokenIdentifier, minutesTtl }) => ({
        tokenIdentifier,
        minutesTtl,
      }))
    );
    if (firstValidToken) {
      await fetch(`${envVariables.VITE_WEBSERVER_API_URL}/user/logout`, {
        method: 'POST',
        body: requestBody,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${firstValidToken}`,
        },
      }).catch(err => {
        console.error(`${ErrorTypes.Authentication}: logout failed: ${err}`);
        if (retry < 1) {
          blackListTokens(tokenMap, retry + 1);
        }
        setIsLoading(false);
      });
    }
  }

  const logoutFromAvalor = async ({ logout, loginWithRedirect, redirectBack }) => {
    localStorage.removeItem(AuthStorageKeys.accountId);
    if (redirectBack) {
      await loginWithRedirect(window.location.pathname + window.location.search);
    } else {
      datadogRum.addAction(`logout action was performed`, { stacktrace: new Error().stack });
      setIsLoading(true);
      const tokenMap = getParsedAccountTokenMap();
      localStorage.removeItem(AuthStorageKeys.accessTokenByAccountId);
      localStorage.removeItem(AuthStorageKeys.sessionByAccountId);
      localStorage.removeItem(AuthStorageKeys.authConfig);
      await blackListTokens(tokenMap);
      sharedWorkerService.notifyWorkerOnLogoutEvent();
      sharedWorkerService.terminate();
      await logout();
    }
  };

  const logout = (redirectBack = false) =>
    logoutFromAvalor({ logout: signOut, loginWithRedirect: isZidSupported() ? authPlatform.signIn : signIn, redirectBack });

  const getAccessToken = (accountId, forceRefetch = false) => getAccountToken(accountId, forceRefetch);

  const fetchToken = async (accountId: string, forceRefetch: boolean) =>
    globalThis.navigator.locks.request(`refetch-${accountId}`, { mode: 'exclusive' }, async () => {
      const token = await getAccessToken(accountId, forceRefetch);
      return token;
    });

  const refreshAccessToken = async (accountId: string, forceRefetch = false) => {
    if (navigator.onLine) {
      return fetchToken(accountId, forceRefetch);
    }

    return new Promise(resolve => {
      const handleOnline = async () => {
        window.removeEventListener('online', handleOnline);
        resolve(await fetchToken(accountId, forceRefetch));
      };
      window.addEventListener('online', handleOnline);
    });
  };

  return {
    getAccessToken,
    logout,
    refreshAccessToken,
    blackListTokens,
    authPlatform,
  };
};

export type AuthContextType = {
  getAccessToken: (accountId: string) => Promise<string>;
  logout: (redirectBack?: any) => Promise<void>;
  refreshAccessToken: (accountId: string, forceRefetch?: boolean) => Promise<string>;
  blackListTokens: (arg: any) => void;
  authPlatform: AuthenticationProvider;
};
